import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign, Plus, ChevronRight, CheckCircle, CreditCard,
  Trash2, Users, Clock, TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';

interface PayrollLine {
  id: string;
  userId: string;
  salaryType?: string;
  hoursWorked?: number;
  daysPresent?: number;
  commissionAmount?: number;
  grossPay?: number;
  deductions?: number;
  netPay?: number;
  user?: { firstName?: string; lastName?: string };
  // legacy field names
  userName?: string;
  designation?: string;
  baseSalary?: number;
  bonusAmount?: number;
  deductionsAmount?: number;
  netPayable?: number;
}

interface PayrollRun {
  id: string;
  runNumber: string;
  status: 'DRAFT' | 'APPROVED' | 'PAID';
  periodStart: string;
  periodEnd: string;
  totalAmount?: number;
  totalGross?: number;
  totalDeductions?: number;
  totalNet?: number;
  notes?: string;
  createdAt: string;
  lines?: PayrollLine[];
}

export function PayrollPage() {
  const { t } = useTranslation('employees');
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('payroll:write') || hasPermission('payroll:*');
  const canApprove = hasPermission('payroll:approve') || hasPermission('payroll:*');
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState<PayrollRun | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<string | null>(null);
  const [confirmPay, setConfirmPay] = useState<string | null>(null);

  const [genForm, setGenForm] = useState({ periodStart: '', periodEnd: '', notes: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['payroll-runs', page, statusFilter],
    queryFn: () => api.get('/payroll', { params: { page, limit: 15, status: statusFilter || undefined } }).then(r => r.data),
  });

  const { data: runDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['payroll-run', selectedRun?.id],
    queryFn: () => api.get(`/payroll/${selectedRun!.id}`).then(r => r.data),
    enabled: !!selectedRun,
  });

  const generateMutation = useMutation({
    mutationFn: () => api.post<PayrollRun>('/payroll/generate', genForm),
    onSuccess: (res) => {
      toast.success('Payroll run generated');
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
      setGenerateOpen(false);
      setSelectedRun(res.data);
      setPreview(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => toast.error(err.response?.data?.message ?? 'Failed to generate payroll'),
  });

  const [preview, setPreview] = useState<null | {
    periodStart: string;
    periodEnd: string;
    totalEmployees: number;
    totalAmount: number;
    lines: Array<{ userId: string; userName: string; hoursWorked: number; daysPresent: number; grossPay: number; commissionAmount: number; netPay: number }>;
  }>(null);
  const previewMutation = useMutation({
    mutationFn: () => api.post('/payroll/preview', genForm).then((r) => r.data),
    onSuccess: (data) => setPreview(data),
    onError: (err: { response?: { data?: { message?: string } } }) => toast.error(err.response?.data?.message ?? 'Preview failed'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post(`/payroll/${id}/approve`),
    onSuccess: () => { toast.success('Payroll run approved'); qc.invalidateQueries({ queryKey: ['payroll-runs'] }); qc.invalidateQueries({ queryKey: ['payroll-run', selectedRun?.id] }); },
    onError: (err: { response?: { data?: { message?: string } } }) => toast.error(err.response?.data?.message ?? 'Failed to approve'),
  });

  const payMutation = useMutation({
    mutationFn: (id: string) => api.post(`/payroll/${id}/pay`),
    onSuccess: () => { toast.success('Payroll marked as paid'); qc.invalidateQueries({ queryKey: ['payroll-runs'] }); qc.invalidateQueries({ queryKey: ['payroll-run', selectedRun?.id] }); },
    onError: (err: { response?: { data?: { message?: string } } }) => toast.error(err.response?.data?.message ?? 'Failed to mark paid'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/payroll/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['payroll-runs'] }); if (selectedRun?.id === confirmDelete) setSelectedRun(null); },
    onError: (err: { response?: { data?: { message?: string } } }) => toast.error(err.response?.data?.message ?? 'Failed to delete'),
  });

  const runs: PayrollRun[] = data?.data ?? [];
  const total = data?.total ?? 0;

  const statusBg: Record<string, string> = {
    DRAFT: 'bg-muted text-muted-foreground',
    APPROVED: 'bg-info/10 text-info-text',
    PAID: 'bg-success/10 text-success-text',
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Payroll"
        subtitle="Generate and manage staff payroll runs"
        action={canWrite && (
          <Button onClick={() => { setGenForm({ periodStart: '', periodEnd: '', notes: '' }); setGenerateOpen(true); }}>
            <Plus className="h-4 w-4 me-2" />
            Generate Payroll Run
          </Button>
        )}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: list */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-success-text" /> Payroll Runs
                </CardTitle>
                <Dropdown
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.value); setPage(1); }}
                  options={[
                    { label: 'All statuses', value: '' },
                    { label: 'Draft', value: 'DRAFT' },
                    { label: 'Approved', value: 'APPROVED' },
                    { label: 'Paid', value: 'PAID' },
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="All statuses"
                />
              </div>
            </CardHeader>
            <CardContent className="px-3">
              {isLoading ? <SkeletonTable rows={5} cols={3} /> : runs.length === 0 ? (
                <EmptyState
                  icon={DollarSign}
                  title="No payroll runs"
                  description="Generate your first payroll run for a period"
                  action={canWrite ? { label: 'Generate', onClick: () => setGenerateOpen(true) } : undefined}
                />
              ) : (
                <div className="space-y-1">
                  {runs.map((run) => (
                    <button
                      key={run.id}
                      onClick={() => setSelectedRun(run)}
                      className={`w-full rounded-lg px-3 py-2.5 text-start transition-all hover:bg-accent/50 ${selectedRun?.id === run.id ? 'bg-primary/10 border border-primary/30' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {formatDate(run.periodStart)} – {formatDate(run.periodEnd)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {run.lines?.length ?? 0} employees · {formatCurrency(Number(run.totalAmount))}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBg[run.status] ?? ''}`}>
                            {run.status}
                          </span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <PaginationBar page={page} pageSize={15} total={total} onPageChange={setPage} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: detail */}
        <div className="lg:col-span-3">
          {!selectedRun ? (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="py-16 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium">Select a payroll run</p>
                <p className="text-xs text-muted-foreground mt-1">Click a run on the left to view details</p>
              </CardContent>
            </Card>
          ) : detailLoading ? (
            <Card><CardContent className="p-6"><SkeletonTable rows={6} cols={5} /></CardContent></Card>
          ) : runDetail ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {formatDate(runDetail.periodStart)} – {formatDate(runDetail.periodEnd)}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Generated {formatDate(runDetail.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBg[runDetail.status] ?? ''}`}>
                      {runDetail.status}
                    </span>
                    {canApprove && runDetail.status === 'DRAFT' && (
                      <Button size="sm" onClick={() => setConfirmApprove(runDetail.id)} disabled={approveMutation.isPending}>
                        <CheckCircle className="h-3.5 w-3.5 me-1" /> Approve
                      </Button>
                    )}
                    {canApprove && runDetail.status === 'APPROVED' && (
                      <Button size="sm" onClick={() => setConfirmPay(runDetail.id)} disabled={payMutation.isPending}>
                        <CreditCard className="h-3.5 w-3.5 me-1" /> Mark Paid
                      </Button>
                    )}
                    {canWrite && runDetail.status === 'DRAFT' && (
                      <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(runDetail.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { icon: Users, label: 'Employees', value: runDetail.lines?.length ?? 0 },
                    { icon: Clock, label: 'Total Hours', value: (runDetail.lines ?? []).reduce((s: number, l: PayrollLine) => s + Number(l.hoursWorked ?? 0), 0).toFixed(1) },
                    { icon: TrendingUp, label: 'Total Payout', value: formatCurrency(Number(runDetail.totalAmount)) },
                  ].map(({ icon: Icon, label, value }, i) => (
                    <div key={i} className="rounded-lg border bg-muted/30 p-3 text-center">
                      <Icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-base font-bold leading-tight">{value}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      {(['colEmployee', 'colType', 'colHours', 'colDays', 'colCommission', 'colGross', 'colDeductions', 'colNetPay'] as const).map((h) => (
                        <Th key={h} className={h === 'colEmployee' || h === 'colType' ? 'text-start' : 'text-end'}>{t(h)}</Th>
                      ))}
                    </Tr>
                  </THead>
                  <TBody>
                    {(runDetail.lines ?? []).map((line: PayrollLine) => (
                      <Tr key={line.id}>
                        <Td label={t('colEmployee')} className="font-medium">{line.user?.firstName} {line.user?.lastName}</Td>
                        <Td label={t('colType')}><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{line.salaryType}</span></Td>
                        <Td label={t('colHours')} className="text-end">{Number(line.hoursWorked).toFixed(1)}</Td>
                        <Td label={t('colDays')} className="text-end">{line.daysPresent}</Td>
                        <Td label={t('colCommission')} className="text-end text-success-text">{formatCurrency(Number(line.commissionAmount))}</Td>
                        <Td label={t('colGross')} className="text-end">{formatCurrency(Number(line.grossPay))}</Td>
                        <Td label={t('colDeductions')} className="text-end text-destructive-text">{formatCurrency(Number(line.deductions))}</Td>
                        <Td label={t('colNetPay')} className="text-end font-semibold text-success-text">{formatCurrency(Number(line.netPay))}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Generate Dialog */}
      <Dialog visible={generateOpen} onHide={() => setGenerateOpen(false)} header="Generate Payroll Run" modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }} style={{ width: '40vw' }}>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Period Start *</Label>
            <Input type="date" value={genForm.periodStart} onChange={(e) => setGenForm((f) => ({ ...f, periodStart: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label>Period End *</Label>
            <Input type="date" value={genForm.periodEnd} onChange={(e) => setGenForm((f) => ({ ...f, periodEnd: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={genForm.notes} onChange={(e) => setGenForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. May 2025 payroll" className="mt-1" />
          </div>
          <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
            Calculates pay for all active users with a salary configured, based on time entries in the selected period.
          </p>

          {preview && (
            <div className="border rounded-lg p-3 bg-muted/30 mt-2">
              <p className="text-sm font-semibold mb-2">Preview — {preview.totalEmployees} employees · Total: <span className="text-primary-text">{preview.totalAmount.toLocaleString()}</span></p>
              <div className="max-h-48 overflow-y-auto">
                <Table mobileCards className="text-xs">
                  <THead>
                    <Tr hover={false}><Th className="px-2 py-2 text-start">{t('colEmployee')}</Th><Th className="px-2 py-2 text-end">{t('colHours')}</Th><Th className="px-2 py-2 text-end">{t('colDays')}</Th><Th className="px-2 py-2 text-end">{t('colGross')}</Th><Th className="px-2 py-2 text-end">{t('colNet')}</Th></Tr>
                  </THead>
                  <TBody>
                    {preview.lines.map((l) => (
                      <Tr key={l.userId}><Td label={t('colEmployee')} className="px-2 py-2">{l.userName}</Td><Td label={t('colHours')} className="px-2 py-2 text-end">{l.hoursWorked}</Td><Td label={t('colDays')} className="px-2 py-2 text-end">{l.daysPresent}</Td><Td label={t('colGross')} className="px-2 py-2 text-end">{l.grossPay.toLocaleString()}</Td><Td label={t('colNet')} className="px-2 py-2 text-end font-medium">{l.netPay.toLocaleString()}</Td></Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setGenerateOpen(false); setPreview(null); }}>Cancel</Button>
            <Button variant="outline" onClick={() => { if (!genForm.periodStart || !genForm.periodEnd) { toast.error('Period dates are required'); return; } previewMutation.mutate(); }} disabled={previewMutation.isPending}>
              {previewMutation.isPending ? 'Calculating…' : 'Preview'}
            </Button>
            <Button onClick={() => { if (!genForm.periodStart || !genForm.periodEnd) { toast.error('Period dates are required'); return; } generateMutation.mutate(); }} disabled={generateMutation.isPending}>
              {generateMutation.isPending ? 'Generating…' : 'Generate'}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog open={!!confirmApprove} onCancel={() => setConfirmApprove(null)}
        onConfirm={() => { approveMutation.mutate(confirmApprove!); setConfirmApprove(null); }}
        title="Approve Payroll Run" message="Lock the run for payment processing." confirmLabel="Approve" variant="warning" />
      <ConfirmDialog open={!!confirmPay} onCancel={() => setConfirmPay(null)}
        onConfirm={() => { payMutation.mutate(confirmPay!); setConfirmPay(null); }}
        title="Mark as Paid" message="Confirm all employees have been paid." confirmLabel="Mark Paid" variant="warning" />
      <ConfirmDialog open={!!confirmDelete} onCancel={() => setConfirmDelete(null)}
        onConfirm={() => { deleteMutation.mutate(confirmDelete!); setConfirmDelete(null); }}
        title="Delete Payroll Run" message="This action cannot be undone." confirmLabel="Delete" variant="danger" />
    </div>
  );
}
